import { Users } from 'lucide-react';

import { RestaurantPreparationStationDTO } from '../../../../types/restaurant/RestaurantPreparationStationTypes';
import RestaurantTable, {
  RestaurantTableColumn,
} from '../../shared/RestaurantTable';
import RestaurantTableActions from '../../shared/RestaurantTableActions';

interface Props {
  stations: RestaurantPreparationStationDTO[];
  onEdit: (station: RestaurantPreparationStationDTO) => void;
  onDeactivate: (station: RestaurantPreparationStationDTO) => void;
  onManageAssignments: (station: RestaurantPreparationStationDTO) => void;
}

const statusPill = (station: RestaurantPreparationStationDTO) => {
  if (!station.active) {
    return { label: 'Inactiva', cls: 'border-danger/20 bg-danger/10 text-danger' };
  }
  if (!station.enabled) {
    return { label: 'Pausada', cls: 'border-warning/30 bg-warning/10 text-warning' };
  }
  return { label: 'Operativa', cls: 'border-success/20 bg-success/10 text-success' };
};

const PreparationStationTable = ({
  stations,
  onEdit,
  onDeactivate,
  onManageAssignments,
}: Props) => {
  const columns: RestaurantTableColumn<RestaurantPreparationStationDTO>[] = [
    {
      key: 'order',
      header: 'Orden',
      className: 'text-center font-semibold text-black dark:text-white',
      headClassName: 'text-center',
      cell: (s) => s.displayOrder,
    },
    {
      key: 'code',
      header: 'Código',
      className: 'whitespace-nowrap font-semibold text-primary',
      cell: (s) => s.code,
    },
    {
      key: 'name',
      header: 'Nombre',
      className: 'font-medium text-black dark:text-white',
      cell: (s) => s.name,
    },
    {
      key: 'printer',
      header: 'Impresora',
      className: 'text-sm text-bodydark2',
      cell: (s) => s.printerName || 'Sin impresora asignada',
    },
    {
      key: 'time',
      header: 'Tiempo estimado',
      className: 'whitespace-nowrap text-sm text-bodydark2',
      cell: (s) =>
        s.estimatedPreparationMinutes
          ? `${s.estimatedPreparationMinutes} min`
          : '—',
    },
    {
      key: 'status',
      header: 'Estado',
      cell: (s) => {
        const pill = statusPill(s);
        return (
          <span
            className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${pill.cls}`}
          >
            {pill.label}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: 'Acciones',
      className: 'text-right',
      headClassName: 'text-right',
      cell: (s) => (
        <RestaurantTableActions
          onEdit={() => onEdit(s)}
          onToggle={s.active ? () => onDeactivate(s) : undefined}
          active={s.active}
          toggleLabel={{ on: 'Desactivar', off: 'Desactivar' }}
          extra={
            <button
              type="button"
              onClick={() => onManageAssignments(s)}
              className="inline-flex items-center gap-2 rounded-xl border border-stroke px-3 py-2 text-sm font-medium transition hover:border-primary hover:bg-primary/5 hover:text-primary dark:border-strokedark"
            >
              <Users className="h-4 w-4" /> Responsables
            </button>
          }
        />
      ),
    },
  ];

  return (
    <RestaurantTable
      columns={columns}
      rows={stations}
      rowKey={(s) => s.id}
      rowClassName={(s) => (s.active ? '' : 'opacity-65')}
      minWidth="min-w-[1050px]"
    />
  );
};

export default PreparationStationTable;
